/**
 * Admin screen to view a transaction and related ledger line entries
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import LoadingSpinner from '../ui/LoadingSpinner';
import PageTitle from '../ui/PageTitle';
import ReceivePaymentMutation from '../customer/ReceivePaymentMutation';
import ServerError from '../ui/ServerError';
import { Button, Grid, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';

const abEnvironment = new AbEnvironment();
const TransactionQuery = graphql`
query TransactionQuery($transactionId: UUID!) {
  transactionTByTransactionId(transactionId: $transactionId) {
    transactionType
    transactionTime
    extSource
    extTransactionId
    description
    transactionRelationTsByRelatedId {
      totalCount
      nodes {
        transactionId
        relatedId
        transactionTByTransactionId {
          transactionType
          transactionTime
          extSource
          extTransactionId
          description
        }
      }
    }
    transactionRelationTsByTransactionId {
      totalCount
      nodes {
        transactionId
        relatedId
        transactionTByRelatedId {
          transactionType
          transactionTime
          extSource
          extTransactionId
          description
        }
      }
    }
    ledgerLineTsByTransactionId {
      totalCount      
      nodes {
        ledgerLineId
        ledgerId
        ledgerTByLedgerId {
          ledgerTypeTByLedgerTypeId {
            balanceType
          }
          ledgerName
          description
          accountId
          accountTByAccountId {
            accountName
          }
        }
        changeType
        changeAmount
      }
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    },
    button: {
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    dateField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 4,
      width: 180,
    },
    textField2: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 3,
      width: 160,
    },
    textFieldSearch: {
      marginRight: theme.spacing(1),
      marginTop: 3,
      marginLeft: 0,
      width: 300,
    },
    paper1: {
      padding: 16,
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    paper2: {
      padding: 12,
      textAlign: 'left',
      color: theme.palette.text.secondary,
    },
    paper3: {
      padding: 16,
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
});

class Transaction extends Component {
  constructor(props) {
    super(props);

    let tDate = new Date();
    let theMonthN = tDate.getMonth() + 1;
    let theMonth = "" + theMonthN;
    if (theMonth.length === 1)
        theMonth = "0" + theMonth;
    
    let theDayN = tDate.getDate();
    let theDay = "" + theDayN;
    if (theDay.length === 1)
        theDay = "0" + theDay;

    let formattedDate = "" + tDate.getFullYear() + "-" +
    theMonth + "-" +
    theDay;

    this.state = {
      paymentAmount: 0,
      feeAmount: 0,
      transdate: formattedDate,
      poid: ''
    }
  }

  goTo(url) {
    if (url == null)
      return;
      
    this.props.history.push(`${url}`);
  }        

  enterPaymentReceipt(extSource, extTransactionId, extPayoutId, bDescription, transactionTime,
                      paymentAmt, feeAmt)
  {
    ReceivePaymentMutation.commit(extSource, extTransactionId, extPayoutId, bDescription, transactionTime,
                                  paymentAmt, feeAmt)
    .then((resp) => {
      // console.log(resp);
      window.location.reload();
    })
    .catch((err) => {
      // console.log(err);
      window.location.reload();
    });
  }

  render() {
    const { isAuthenticatedAppBind, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticatedAppBind()) { 
      return ( <Redirect to='/home' /> );
    }    

    const params = new URLSearchParams(this.props.location.search);
    let transactionId = params.get('transactionId');

    let theTitle ='Transaction';

    return (
      <div className={classes.root}>
        <PageTitle title={theTitle}/>
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={TransactionQuery}
          variables={{transactionId: transactionId}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              let ttype = props.transactionTByTransactionId.transactionType;
              let ttypeText = 'Other';
              if (ttype === 0) {
                ttypeText = 'BindCard Charged';
              } else if (ttype === 1) {
                ttypeText = 'AppBind Billing';
              } else if (ttype === 2) {
                ttypeText = 'Payment Received';
              }

              let tTransactionTime = props.transactionTByTransactionId.transactionTime;
              let tDescription = props.transactionTByTransactionId.description;
              let tExtSource = props.transactionTByTransactionId.extSource;
              let tExtTransactionId = props.transactionTByTransactionId.extTransactionId;

              // let theText = tTransactionTime + " " + ttypeText + ': ' + tDescription + ', ' + tExtSource + ': ' + tExtTransactionId;

              //
              // If this is a billing transaction, and there is no subsequent (payment) transaction,
              // enable manual addition.  This will take total amount and fee amount, and call the receive payment
              // function.  We'll use the external source and external id of this transaction
              // Parms:  extSource, extId, description, time <use null>, atId <hardcode to USD>, chargeAmount, feeAmount
              //
              let allowAddPayment = false;
              if (ttype === 1 && props.transactionTByTransactionId.transactionRelationTsByTransactionId.totalCount === 0) {
                // No related payment.
                allowAddPayment = true;
              }
            
              return (
                <div className={classes.paper}>
                <Grid container spacing={2} direction="row" justifyContent="flex-start">
                  <Grid item xs={2}>
                    <Typography>Date: {tTransactionTime}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography>Type: {ttypeText}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography>Description: {tDescription}</Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography>Ext: {tExtSource}: {tExtTransactionId}</Typography>
                  </Grid>
                  { allowAddPayment &&
                  <Grid item>
                    <TextField
                      id="payment-amount" label="Payment Amount" className={classes.textField}
                      value={this.state.paymentAmount}
                      onChange={(e) => this.setState({ paymentAmount: e.target.value })} />
                  </Grid>
                  }
                  { allowAddPayment &&
                  <Grid item>
                    <TextField
                      id="fee-amount" label="Fee Amount" className={classes.textField}
                      value={this.state.feeAmount}
                      onChange={(e) => this.setState({ feeAmount: e.target.value })} />
                  </Grid>
                  }
                  { allowAddPayment &&
                  <Grid item>
                    <TextField
                      id="transaction-date" label="Transaction Date" className={classes.textField}
                      value={ this.state.transdate }
                      onChange={ (e) => this.setState({ transdate: e.target.value }) }
                    />
                  </Grid>
                  }
                  { allowAddPayment &&
                  <Grid item>
                    <TextField
                      id="poid" label="Payout Id" className={classes.textField}
                      value={ this.state.poid }
                      onChange={ (e) => this.setState({ poid: e.target.value }) }
                    />
                  </Grid>
                  }
                  { allowAddPayment &&
                  <Grid item>
                    <Button variant="contained" color="secondary" className={classes.button} 
                            onClick={() => {
                              this.enterPaymentReceipt(tExtSource, tExtTransactionId, this.state.poid, '',
                              this.state.transdate, this.state.paymentAmount, this.state.feeAmount)}
                            }
                    >
                      Accept Payment
                    </Button>
                  </Grid>
                  }

                  {/* Previous Transactions */}

                  { props.transactionTByTransactionId.transactionRelationTsByRelatedId.totalCount > 0 &&
                  <Grid item xs={12}>
                    <Typography>Previous Transactions</Typography>
                  </Grid>
                  }

                  { props.transactionTByTransactionId.transactionRelationTsByRelatedId.totalCount > 0 &&
                  <Grid item xs={12}>
                  <Table>
                    <TableHead className={classes.tblHead}>
                      <TableRow className={classes.tblHeadRow}>
                        <TableCell className={classes.tblHeadCell}>Previous Transaction Date</TableCell>
                        <TableCell className={classes.tblHeadCell}>Type</TableCell>
                        <TableCell className={classes.tblHeadCell}>Description</TableCell>
                        <TableCell className={classes.tblHeadCell}>Ext Source</TableCell>
                        <TableCell className={classes.tblHeadCell}>Ext Id</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    { props.transactionTByTransactionId.transactionRelationTsByRelatedId.nodes.map(n => {
                      let pTransactionTime  = n.transactionTByTransactionId.transactionTime;
                      let pTransactionType  = n.transactionTByTransactionId.transactionType;
                      let pDescription      = n.transactionTByTransactionId.description;
                      let pExtSource        = n.transactionTByTransactionId.extSource;
                      let pExtTransactionId = n.transactionTByTransactionId.extTransactionId;
                      let pTransactionLink = "transaction?transactionId=" + n.transactionId;

                      return (
                        <TableRow key={n.transactionId}>
                          <TableCell onClick={() => {this.goTo(pTransactionLink);}}>{pTransactionTime}</TableCell>
                          <TableCell>{pTransactionType}</TableCell>
                          <TableCell>{pDescription}</TableCell>
                          <TableCell>{pExtSource}</TableCell>
                          <TableCell>{pExtTransactionId}</TableCell>
                        </TableRow>
                      );
                    })}
                    </TableBody>
                  </Table>
                  </Grid>
                  }

                  {/* Subsequent Transactions */}

                  { props.transactionTByTransactionId.transactionRelationTsByTransactionId.totalCount > 0 &&
                  <Grid item xs={12}>
                    <Typography>Subsequent Transactions</Typography>
                  </Grid>
                  }

                  { props.transactionTByTransactionId.transactionRelationTsByTransactionId.totalCount > 0 &&
                  <Grid item xs={12}>
                  <Table>
                    <TableHead className={classes.tblHead}>
                      <TableRow className={classes.tblHeadRow}>
                        <TableCell className={classes.tblHeadCell}>Next Transaction Date</TableCell>
                        <TableCell className={classes.tblHeadCell}>Type</TableCell>
                        <TableCell className={classes.tblHeadCell}>Description</TableCell>
                        <TableCell className={classes.tblHeadCell}>Ext Source</TableCell>
                        <TableCell className={classes.tblHeadCell}>Ext Id</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    { props.transactionTByTransactionId.transactionRelationTsByTransactionId.nodes.map(n => {
                      let nTransactionTime  = n.transactionTByRelatedId.transactionTime;
                      let nTransactionType  = n.transactionTByRelatedId.transactionType;
                      let nDescription      = n.transactionTByRelatedId.description;
                      let nExtSource        = n.transactionTByRelatedId.extSource;
                      let nExtTransactionId = n.transactionTByRelatedId.extTransactionId;
                      let nTransactionLink  = "transaction?transactionId=" + n.relatedId;

                      return (
                        <TableRow key={n.transactionId}>
                          <TableCell onClick={() => {this.goTo(nTransactionLink);}}>{nTransactionTime}</TableCell>
                          <TableCell>{nTransactionType}</TableCell>
                          <TableCell>{nDescription}</TableCell>
                          <TableCell>{nExtSource}</TableCell>
                          <TableCell>{nExtTransactionId}</TableCell>
                        </TableRow>
                      );
                    })}
                    </TableBody>
                  </Table>
                  </Grid>
                  }

                  <Grid item xs={12}>
                    <Typography>Ledger Entries</Typography>
                  </Grid>
                  {/* Ledger Lines */}
                  <Grid item xs={12}>
                  <Table>
                    <TableHead className={classes.tblHead}>
                      <TableRow className={classes.tblHeadRow}>
                        <TableCell className={classes.tblHeadCell}>Account</TableCell>
                        <TableCell className={classes.tblHeadCell}>Ledger</TableCell>
                        <TableCell className={classes.tblHeadCell}>Line</TableCell>
                        <TableCell className={classes.tblHeadCell}>Type</TableCell>
                        <TableCell className={classes.tblHeadCell}>Debit Amt</TableCell>
                        <TableCell className={classes.tblHeadCell}>Credit Amt</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    { props.transactionTByTransactionId.ledgerLineTsByTransactionId.nodes.map(n => {
                      // Balance type 0 is debit normal, 1 is credit normal.
                      let bType = n.ledgerTByLedgerId.ledgerTypeTByLedgerTypeId.balanceType;
                      let debitAmount = 0;
                      let creditAmount = 0;
                      if (bType === 0) {
                        if (n.changeAmount > 0) {
                          debitAmount = n.changeAmount;
                        } else {
                          creditAmount = -1 * n.changeAmount;
                        }
                      } else {
                        if (n.changeAmount > 0) {
                          creditAmount = n.changeAmount;
                        } else {
                          debitAmount = -1 * n.changeAmount;
                        }
                      }

                      let accountName = n.ledgerTByLedgerId.accountTByAccountId.accountName;
                      let accountLink = "accountledgers?accountId=" + n.ledgerTByLedgerId.accountId + "&accountName=" + accountName;
                      let ledgerName = n.ledgerTByLedgerId.ledgerName;
                      let ledgerLink = "ledger?ledgerId=" + n.ledgerId + '&ledgerName=' + ledgerName + '&accountName=' + accountName;
                      return (
                        <TableRow key={n.ledgerLineId}>
                            <TableCell onClick={() => {this.goTo(accountLink);}}>{accountName}</TableCell>
                            <TableCell onClick={() => {this.goTo(ledgerLink);}}>{ledgerName}</TableCell>
                            <TableCell>{n.ledgerTByLedgerId.description}</TableCell>
                            <TableCell>{n.changeType}</TableCell>
                            <TableCell align="right">{debitAmount}</TableCell>
                            <TableCell align="right">{creditAmount}</TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </Grid>
                </Grid>
                </div>
              )
            }
            return (
              <LoadingSpinner />
            );
          }}
        />
      </div>
    );
  }
}
  
Transaction.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(Transaction);
